import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useGlobalContext } from "../../GlobalContext";
import useChargesAndVehicles from "../../Sevices/useChargesAndVehicles";
import VehicleFareList from "./VehicleFareList";

const formatAmount = (value) => {
  const amount = Number(value || 0);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
};

function ChargesAndVechicle() {
  const {
    fetchVehicleDetailsApi,
    fetchDistanceFaresApi,
    createVehicleApi,
    deleteVehicleApi,
    saveDistanceFareApi,
    deleteDistanceFareApi,
    getPackerSlotDetailsApi,
  } = useChargesAndVehicles();

  const [vehicles, setVehicles] = useState([]);
  const [fares, setFares] = useState([]);

  const [loading, setLoading] = useState(false);
  const [vehicleSaving, setVehicleSaving] = useState(false);
  const [fareSavingId, setFareSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteVehicle, setSelectedDeleteVehicle] = useState(null);
  const [expandedVehicleId, setExpandedVehicleId] = useState(null);
  const [distanceDeleteModalOpen, setDistanceDeleteModalOpen] = useState(false);
  const [selectedDistanceFare, setSelectedDistanceFare] = useState(null);
  const [deletingDistanceFareId, setDeletingDistanceFareId] = useState(null);
  const [packerSlotGroups, setPackerSlotGroups] = useState([]);
  const [selectedSlotDate, setSelectedSlotDate] = useState("");

  const [vehicleForm, setVehicleForm] = useState({
    name: "",
    description: "",
  });

  const [fareForms, setFareForms] = useState({});
  const { AdminId } = useGlobalContext();

  const normalizePackerSlotGroups = (apiResponse) => {
    const data =
      apiResponse?.data || apiResponse?.response || apiResponse?.result || [];

    const groups = Array.isArray(data) ? data : [];

    return groups.map((group) => ({
      date: String(group?.date || ""),
      slots: Array.isArray(group?.slotDetails)
        ? group.slotDetails.map((slot) => ({
            slotId: String(slot?.pk_slot_id || ""),
            slotTime: String(slot?.slot_time || ""),
            status: String(slot?.status || ""),
            isActive: String(slot?.status || "").toUpperCase() === "Y",
            createdOn: slot?.created_on || "",
            raw: slot,
          }))
        : [],
    }));
  };

  const latestFareByVehicleId = useMemo(() => {
    const map = {};

    fares.forEach((fareGroup) => {
      const vehicleId = String(fareGroup.fk_vehicle_id);

      const distanceList = Array.isArray(fareGroup.distance)
        ? fareGroup.distance
        : [];

      if (distanceList.length === 0) return;

      const latestFare = distanceList.reduce((latest, current) => {
        if (
          !latest ||
          Number(current.pk_distance_fare_id) >
            Number(latest.pk_distance_fare_id)
        ) {
          return current;
        }

        return latest;
      }, null);

      if (latestFare) {
        map[vehicleId] = latestFare;
      }
    });

    return map;
  }, [fares]);

  const distanceFareByVehicleId = useMemo(() => {
    const map = {};

    fares.forEach((fareGroup) => {
      const vehicleId = String(fareGroup.fk_vehicle_id);

      const distanceList = Array.isArray(fareGroup.distance)
        ? [...fareGroup.distance]
        : [];

      map[vehicleId] = distanceList.sort(
        (a, b) =>
          Number(a.minimum_distance || 0) - Number(b.minimum_distance || 0),
      );
    });

    return map;
  }, [fares]);

  const handleEditDistanceSlab = (vehicleId, slab) => {
    setFareForms((prev) => ({
      ...prev,
      [vehicleId]: {
        minimumDistance: String(slab.minimum_distance ?? 0),
        maximumDistance: String(slab.maximum_distance ?? ""),
        fare: String(slab.fare ?? ""),
        rainyWeather: String(slab.rainy_weather_charge ?? ""),
        nightCharge: String(slab.night_charge ?? ""),
        surcharge: String(slab.surcharge ?? ""),
        additionalCharge: String(slab.additional_charge ?? ""),
        pkDistanceFareId: String(slab.pk_distance_fare_id ?? ""),
      },
    }));
  };

  const toggleVehicleFareTable = (vehicleId) => {
    setExpandedVehicleId((prev) =>
      String(prev) === String(vehicleId) ? null : vehicleId,
    );
  };

  const loadPackerSlots = async () => {
    const result = await getPackerSlotDetailsApi({
      userId: String(AdminId),
    });

    const groups = normalizePackerSlotGroups(result);

    setPackerSlotGroups(groups);

    if (!selectedSlotDate && groups[0]?.date) {
      setSelectedSlotDate(groups[0].date);
    }

    return groups;
  };

  const loadAll = async (silent = false) => {
    if (!silent) setLoading(true);

    await Promise.all([
      fetchVehicleDetailsApi(setVehicles, setLoading, true),
      fetchDistanceFaresApi(setFares, setLoading, true),
      loadPackerSlots(),
    ]);

    if (!silent) setLoading(false);
  };

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    const nextForms = {};

    vehicles.forEach((vehicle) => {
      const fare = latestFareByVehicleId[String(vehicle.pk_vehicle_id)];

      nextForms[vehicle.pk_vehicle_id] = {
        minimumDistance:
          fare?.minimumDistance !== undefined && fare?.minimumDistance !== null
            ? String(fare.minimumDistance)
            : fare?.minimum_distance !== undefined &&
                fare?.minimum_distance !== null
              ? String(fare.minimum_distance)
              : "0",

        maximumDistance:
          fare?.maximumDistance !== undefined && fare?.maximumDistance !== null
            ? String(fare.maximumDistance)
            : fare?.maximum_distance !== undefined &&
                fare?.maximum_distance !== null
              ? String(fare.maximum_distance)
              : "",

        fare:
          fare?.fare !== undefined && fare?.fare !== null
            ? String(fare.fare)
            : "",

        rainyWeather:
          fare?.rainyWeather !== undefined && fare?.rainyWeather !== null
            ? String(fare.rainyWeather)
            : fare?.rainy_weather_charge !== undefined &&
                fare?.rainy_weather_charge !== null
              ? String(fare.rainy_weather_charge)
              : "",

        nightCharge:
          fare?.nightCharge !== undefined && fare?.nightCharge !== null
            ? String(fare.nightCharge)
            : fare?.night_charge !== undefined && fare?.night_charge !== null
              ? String(fare.night_charge)
              : "",

        surcharge:
          fare?.surcharge !== undefined && fare?.surcharge !== null
            ? String(fare.surcharge)
            : "",

        additionalCharge:
          fare?.additionalCharge !== undefined &&
          fare?.additionalCharge !== null
            ? String(fare.additionalCharge)
            : fare?.additional_charge !== undefined &&
                fare?.additional_charge !== null
              ? String(fare.additional_charge)
              : "",
      };
    });

    setFareForms(nextForms);
  }, [vehicles, latestFareByVehicleId]);

  const handleVehicleInput = (key, value) => {
    setVehicleForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFareInput = (vehicleId, key, value) => {
    setFareForms((prev) => ({
      ...prev,
      [vehicleId]: {
        ...prev[vehicleId],
        [key]: value,
      },
    }));
  };

  const handleAddVehicle = () => {
    if (!vehicleForm.name.trim()) return;

    createVehicleApi(
      {
        name: vehicleForm.name.trim(),
        description: vehicleForm.description.trim(),
      },
      setVehicleSaving,
      () => {
        setVehicleForm({
          name: "",
          description: "",
        });

        loadAll(true);
      },
    );
  };

  const handleDeleteVehicle = (vehicle) => {
    setSelectedDeleteVehicle(vehicle);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deletingId) return;

    setDeleteModalOpen(false);
    setSelectedDeleteVehicle(null);
  };

  const confirmDeleteVehicle = () => {
    if (!selectedDeleteVehicle) return;

    setDeletingId(selectedDeleteVehicle.pk_vehicle_id);

    deleteVehicleApi(selectedDeleteVehicle.pk_vehicle_id, setDeletingId, () => {
      loadAll(true);
      setDeletingId(null);
      setDeleteModalOpen(false);
      setSelectedDeleteVehicle(null);
    });
  };

  const handleDeleteDistanceFare = (vehicle, slab) => {
    setSelectedDistanceFare({
      vehicle,
      slab,
    });

    setDistanceDeleteModalOpen(true);
  };

  const closeDistanceDeleteModal = () => {
    if (deletingDistanceFareId) return;

    setDistanceDeleteModalOpen(false);
    setSelectedDistanceFare(null);
  };

  const confirmDeleteDistanceFare = () => {
    if (!selectedDistanceFare?.slab) return;

    const fareId = selectedDistanceFare.slab.pk_distance_fare_id;

    setDeletingDistanceFareId(fareId);

    deleteDistanceFareApi(
      {
        adminId: String(AdminId),
        id: String(fareId),
      },
      setDeletingDistanceFareId,
      () => {
        loadAll(true);
        setDistanceDeleteModalOpen(false);
        setSelectedDistanceFare(null);
        setDeletingDistanceFareId(null);
      },
    );
  };

  const handleSaveFare = (vehicle) => {
    const form = fareForms[vehicle.pk_vehicle_id] || {};
    const adminId = AdminId;

    if (!form.maximumDistance || !form.fare) return;

    setFareSavingId(vehicle.pk_vehicle_id);

    saveDistanceFareApi(
      {
        adminId: String(adminId),
        vehicleId: String(vehicle.pk_vehicle_id),
        minimumDistance: String(form.minimumDistance || 0),
        maximumDistance: String(form.maximumDistance || 0),
        fare: String(form.fare || 0),
        rainyWeather: String(form.rainyWeather || 0),
        nightCharge: String(form.nightCharge || 0),
        surcharge: String(form.surcharge || 0),
        additionalCharge: String(form.additionalCharge || 0),
      },
      () => {},
      () => {
        loadAll(true);
        setFareSavingId(null);
      },
    );
  };

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      <div className="shrink-0 space-y-6">
        {/* Slim Header */}
        <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Charges And Vechicle
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Configure vehicle types and distance based delivery fare.
            </p>
          </div>

          <button
            type="button"
            onClick={() => loadAll()}
            disabled={loading}
            className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <RefreshCw size={15} />
            )}
            Refresh
          </button>
        </div>

        {/* Add Vehicle */}
        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Add Vehicle</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Add a new delivery vehicle option.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-[1fr_1.5fr_auto]">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Vehicle Name
              </label>
              <input
                value={vehicleForm.name}
                onChange={(e) => handleVehicleInput("name", e.target.value)}
                placeholder="Example: 2 Wheeler"
                className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Description
              </label>
              <input
                value={vehicleForm.description}
                onChange={(e) =>
                  handleVehicleInput("description", e.target.value)
                }
                placeholder="Optional"
                className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
              />
            </div>

            <div className="flex items-end">
              <button
                type="button"
                onClick={handleAddVehicle}
                disabled={vehicleSaving || !vehicleForm.name.trim()}
                className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-orange-500 px-4 text-sm font-medium text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
              >
                {vehicleSaving ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Plus size={15} />
                )}
                Add
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Vehicle Fare List */}
      <VehicleFareList
        vehicles={vehicles}
        fares={fares}
        loading={loading}
        latestFareByVehicleId={latestFareByVehicleId}
        distanceFareByVehicleId={distanceFareByVehicleId}
        fareForms={fareForms}
        expandedVehicleId={expandedVehicleId}
        fareSavingId={fareSavingId}
        deletingId={deletingId}
        deletingDistanceFareId={deletingDistanceFareId}
        toggleVehicleFareTable={toggleVehicleFareTable}
        handleFareInput={handleFareInput}
        handleSaveFare={handleSaveFare}
        handleDeleteVehicle={handleDeleteVehicle}
        handleEditDistanceSlab={handleEditDistanceSlab}
        handleDeleteDistanceFare={handleDeleteDistanceFare}
        formatAmount={formatAmount}
      />

      {/* delete selected vehicle */}

      {deleteModalOpen && selectedDeleteVehicle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeDeleteModal}
          />

          {/* Modal */}
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                <Trash2 size={22} />
              </div>

              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">
                  Delete vehicle?
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold text-gray-900">
                    {selectedDeleteVehicle.name}
                  </span>
                  ? This vehicle will be removed from your settings.
                </p>

                <div className="mt-4 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  This action may also affect fare configuration linked with
                  this vehicle.
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDeleteModal}
                disabled={!!deletingId}
                className="inline-flex h-9 items-center justify-center rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDeleteVehicle}
                disabled={!!deletingId}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-red-600 px-4 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingId ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={15} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* delete selected distance fare */}

      {distanceDeleteModalOpen && selectedDistanceFare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeDistanceDeleteModal}
          />

          {/* Modal */}
          <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
                <Trash2 size={22} />
              </div>

              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">
                  Delete distance fare?
                </h3>

                <p className="mt-1 text-sm text-gray-500">
                  Are you sure you want to delete this distance fare slab for{" "}
                  <span className="font-semibold text-gray-900">
                    {selectedDistanceFare.vehicle?.name}
                  </span>
                  ?
                </p>

                <div className="mt-4 rounded-lg border border-gray-100 bg-gray-50 px-3 py-3 text-xs text-gray-700">
                  <div className="flex justify-between">
                    <span>Distance</span>
                    <span className="font-semibold">
                      {formatAmount(
                        selectedDistanceFare.slab?.minimum_distance,
                      )}{" "}
                      km -{" "}
                      {formatAmount(
                        selectedDistanceFare.slab?.maximum_distance,
                      )}{" "}
                      km
                    </span>
                  </div>

                  <div className="mt-1 flex justify-between">
                    <span>Fare</span>
                    <span className="font-semibold">
                      ₹{formatAmount(selectedDistanceFare.slab?.fare)}
                    </span>
                  </div>
                </div>

                <div className="mt-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  This will delete only this selected distance slab, not the
                  vehicle.
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDistanceDeleteModal}
                disabled={!!deletingDistanceFareId}
                className="inline-flex h-9 items-center justify-center rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmDeleteDistanceFare}
                disabled={!!deletingDistanceFareId}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-red-600 px-4 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingDistanceFareId ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={15} />
                    Delete Slab
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChargesAndVechicle;
